import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from '../hooks/useInView';
import { NUMERO_RENAPDIS, URL_RENAPDIS } from '../utils/renapdis';
import { CheckCircle2, Calendar, Clock, MapPin, Bell, DollarSign, Image, CalendarCheck, UserCheck, Check, Hourglass, CalendarPlus } from 'lucide-react';

// =====================================================
// Componente reutilizable para secciones animadas
// =====================================================
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  animation?: string;
  delay?: number;
}> = ({ children, className = '', animation = 'scroll-fade-up', delay = 0 }) => {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <div
      ref={ref}
      className={`${animation} ${inView ? 'in-view' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// =====================================================
// 1. SECCIÓN: "Listo en 5 minutos" (estilo Calú)
// =====================================================
export const OnboardingSteps: React.FC = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: 'Creás tu cuenta',
      time: '1 minuto',
      description: 'Elegís tu rubro (salud, estética, bienestar) y completás tus datos. consultAR ya viene preparado para tu tipo de servicio.',
    },
    {
      number: 2,
      title: 'Configurás tu agenda',
      time: '2 minutos',
      description: 'Definís días, horarios, duración de turnos y servicios. Nada técnico, nada largo. Todo visual e intuitivo.',
    },
    {
      number: 3,
      title: 'Empezás a recibir turnos',
      time: '¡Listo!',
      description: 'Tu agenda se ordena sola: recordatorios por WhatsApp, confirmaciones automáticas y cobros integrados. Vos solo atendés.',
    },
  ];

  return (
    <section className="relative py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-block text-blue-600 font-bold text-sm tracking-wider uppercase mb-3">
            Implementar consultAR es muy fácil
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            Listo para usar en 5 minutos.
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">Literal.</span>
          </h2>
        </AnimatedSection>

        <div ref={ref} className="relative max-w-3xl mx-auto">
          {/* Línea vertical */}
          <div className="absolute left-8 sm:left-10 top-0 bottom-0 w-0.5 bg-gray-200">
            <div
              className={`w-full bg-gradient-to-b from-blue-500 via-green-500 to-emerald-500 rounded-full transition-all duration-[2000ms] ease-out`}
              style={{ height: inView ? '100%' : '0%' }}
            />
          </div>

          <div className="space-y-12 sm:space-y-16">
            {steps.map((step, index) => (
              <AnimatedSection key={step.number} delay={index * 200} className="relative flex items-start gap-6 sm:gap-8">
                {/* Número */}
                <div className="relative z-10 flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <span className="text-white font-bold text-xl sm:text-2xl">{step.number}</span>
                </div>

                {/* Contenido */}
                <div className="pt-2 sm:pt-3">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                    {step.title} <span className="text-blue-600">→ {step.time}</span>
                  </h3>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        <AnimatedSection className="text-center mt-16" delay={600}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Empezar ahora
            </button>
            <button
              onClick={() => navigate('/search')}
              className="text-gray-700 font-semibold text-lg hover:text-blue-600 transition-colors flex items-center gap-2"
            >
              Ver profesionales
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// =====================================================
// 2. SECCIÓN: Features detalladas con mockups
// =====================================================

interface FeatureRowProps {
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  mockup: React.ReactNode;
  reverse?: boolean;
  gradient: string;
}

const FeatureRow: React.FC<FeatureRowProps> = ({ title, subtitle, description, features, mockup, reverse, gradient }) => {
  const [ref, inView] = useInView({ threshold: 0.15 });

  return (
    <div ref={ref} className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}>
      {/* Texto */}
      <div className={`flex-1 ${reverse ? 'scroll-fade-right' : 'scroll-fade-left'} ${inView ? 'in-view' : ''}`}>
        <span className={`inline-block text-sm font-bold tracking-wider uppercase mb-3 bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
          {subtitle}
        </span>
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
          {title}
        </h3>
        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
          {description}
        </p>
        <ul className="space-y-3">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center mt-0.5`}>
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-gray-700">{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Mockup */}
      <div className={`flex-1 ${reverse ? 'scroll-fade-left' : 'scroll-fade-right'} ${inView ? 'in-view' : ''}`}>
        {mockup}
      </div>
    </div>
  );
};

// Mockup de Agenda
const AgendaMockup = () => (
  <div className="mockup-float">
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-sm mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800">Marzo 2026</h4>
        <div className="flex gap-1">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">‹</div>
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">›</div>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => (
          <div key={i} className="text-blue-600 font-bold py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-sm mb-4">
        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
          <div
            key={day}
            className={`py-1.5 rounded-lg transition-colors ${
              day === 24
                ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white font-bold'
                : day === 20 || day === 27
                ? 'bg-blue-100 text-blue-700 font-medium'
                : [6, 7, 13, 14, 21, 28].includes(day)
                ? 'text-gray-300'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            {day}
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
          <div className="w-1 h-8 bg-green-500 rounded-full" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">09:00 - Camila P.</p>
            <p className="text-xs text-gray-500">Consulta general</p>
          </div>
          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
          <div className="w-1 h-8 bg-blue-500 rounded-full" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">10:30 - Martín G.</p>
            <p className="text-xs text-gray-500">Control mensual</p>
          </div>
          <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2.5">
          <div className="w-1 h-8 bg-purple-500 rounded-full" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">11:45 - Ana R.</p>
            <p className="text-xs text-gray-500">Primera consulta</p>
          </div>
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">1°</div>
        </div>
      </div>
    </div>
  </div>
);

// Mockup de WhatsApp
const WhatsAppMockup = () => (
  <div className="mockup-float" style={{ animationDelay: '1s' }}>
    <div className="bg-[#0b141a] rounded-2xl shadow-2xl max-w-sm mx-auto overflow-hidden">
      {/* Header WhatsApp */}
      <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">cA</div>
        <div>
          <p className="text-white font-medium text-sm">Consultorio Dra. López</p>
          <p className="text-gray-400 text-xs">en línea</p>
        </div>
      </div>
      {/* Chat */}
      <div className="bg-[#0b141a] px-3 py-4 space-y-3" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h40v40H0z\' fill=\'none\'/%3E%3Cpath d=\'M20 20l2-2-2-2-2 2z\' fill=\'rgba(255,255,255,0.02)\'/%3E%3C/svg%3E")' }}>
        {/* Mensaje de confirmación */}
        <div className="max-w-[85%] ml-auto">
          <div className="bg-[#005c4b] rounded-xl rounded-tr-sm px-3 py-2">
            <p className="text-white text-sm">
              Turno confirmado
            </p>
            <p className="text-green-100 text-sm mt-1">
              Hola Camila! Tu turno con la Dra. Lopez fue confirmado.
            </p>
            <p className="text-green-100 text-sm mt-1">
              Lunes 24 de Marzo<br/>
              09:00 hs<br/>
              Av. San Martin 1234
            </p>
            <p className="text-green-200/60 text-[10px] text-right mt-1">09:15 ✓✓</p>
          </div>
        </div>

        {/* Mensaje recordatorio */}
        <div className="max-w-[85%] ml-auto">
          <div className="bg-[#005c4b] rounded-xl rounded-tr-sm px-3 py-2">
            <p className="text-white text-sm">
              Recordatorio
            </p>
            <p className="text-green-100 text-sm mt-1">
              Te recordamos que manana tenes turno con la Dra. Lopez a las 09:00.
            </p>
            <p className="text-green-100 text-sm mt-1">
              Valor: $60.000
            </p>
            <p className="text-green-200/60 text-[10px] text-right mt-1">10:00 ✓✓</p>
          </div>
        </div>

        {/* Respuesta paciente */}
        <div className="max-w-[70%]">
          <div className="bg-[#1f2c34] rounded-xl rounded-tl-sm px-3 py-2">
            <p className="text-white text-sm">Perfecto, ahí estaré! Gracias 😊</p>
            <p className="text-gray-400 text-[10px] text-right mt-1">10:02</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mockup de Historia Clínica
const ClinicalRecordMockup = () => (
  <div className="mockup-float" style={{ animationDelay: '2s' }}>
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-sm mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <p className="font-bold text-gray-800 text-sm">Historia Clínica</p>
          <p className="text-xs text-gray-500">Camila Pérez - HC #0042</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Datos del paciente</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-400">DNI:</span> <span className="font-medium">37.393.301</span></div>
            <div><span className="text-gray-400">Edad:</span> <span className="font-medium">33 años</span></div>
            <div><span className="text-gray-400">O.S:</span> <span className="font-medium">IOSCOR</span></div>
            <div><span className="text-gray-400">Nro:</span> <span className="font-medium">165165165</span></div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-3">
          <p className="text-xs font-semibold text-blue-600 uppercase mb-1">Motivo de consulta</p>
          <p className="text-sm text-gray-700">Control de rutina anual. Refiere dolores de cabeza frecuentes.</p>
        </div>

        <div className="bg-green-50 rounded-xl p-3">
          <p className="text-xs font-semibold text-green-600 uppercase mb-1">Diagnóstico</p>
          <p className="text-sm text-gray-700">Cefalea tensional. Se solicita laboratorio completo.</p>
        </div>

        <div className="bg-purple-50 rounded-xl p-3">
          <p className="text-xs font-semibold text-purple-600 uppercase mb-1">Archivos adjuntos</p>
          <div className="flex gap-2 mt-1">
            <div className="bg-white border border-purple-200 rounded-lg px-2 py-1 text-xs text-purple-600 flex items-center gap-1">
              📄 Laboratorio.pdf
            </div>
            <div className="bg-white border border-purple-200 rounded-lg px-2 py-1 text-xs text-purple-600 flex items-center gap-1">
              Radiografia.jpg
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mockup de Reportes
const ReportsMockup = () => (
  <div className="mockup-float" style={{ animationDelay: '0.5s' }}>
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-sm mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800 text-sm">Resumen del mes</h4>
        <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-1 rounded-full">Marzo 2026</span>
      </div>

      {/* Stats mini */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">48</p>
          <p className="text-[10px] text-gray-500 font-medium">Turnos</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-green-600">92%</p>
          <p className="text-[10px] text-gray-500 font-medium">Asistencia</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-purple-600">12</p>
          <p className="text-[10px] text-gray-500 font-medium">Nuevos</p>
        </div>
      </div>

      {/* Chart bars */}
      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Turnos por día</p>
      <div className="flex items-end gap-1.5 h-24 mb-4">
        {[60, 80, 45, 90, 70, 30, 10, 75, 85, 50, 95, 65, 40, 88].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-gradient-to-t from-blue-500 to-green-400 rounded-t-sm transition-all duration-500 hover:opacity-80"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>

      {/* Ingresos */}
      <div className="bg-gradient-to-r from-blue-500 to-green-500 rounded-xl p-3 text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs opacity-80">Ingresos totales</p>
            <p className="text-xl font-bold">$2.880.000</p>
          </div>
          <div className="text-right">
            <p className="text-xs opacity-80">vs mes anterior</p>
            <p className="text-sm font-bold text-green-200">↑ 15%</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const FeaturesDetailed: React.FC = () => (
  <section className="relative py-20 sm:py-28 bg-gray-50 overflow-hidden">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
      <FeatureRow
        subtitle="Tu agenda online"
        title="Visualizá todo tu calendario en un click"
        description="Con consultAR visualizás tu disponibilidad completa, asignás turnos y dejás que tus pacientes reserven solos. No pierdas ningún turno, ahorrá tiempo y brindá una experiencia profesional."
        features={[
          'Pacientes reservan 24/7 desde cualquier dispositivo',
          'Definís horarios, duración y sedes',
          'Bloqueo de fechas y feriados',
          'Vista diaria, semanal y mensual',
        ]}
        mockup={<AgendaMockup />}
        gradient="from-blue-600 to-cyan-500"
      />

      <FeatureRow
        subtitle="Recordatorios automáticos"
        title="Recordatorios de citas por WhatsApp"
        description="consultAR envía recordatorios automáticos vía WhatsApp Business oficial para disminuir el ausentismo. Optimizá tu tiempo y tu trabajo sin mover un dedo."
        features={[
          'Confirmación instantánea al agendar',
          'Recordatorio 24hs antes del turno',
          'Incluye precio, dirección y horario',
          'Integración oficial con Meta Business',
        ]}
        mockup={<WhatsAppMockup />}
        reverse
        gradient="from-green-500 to-emerald-500"
      />

      <FeatureRow
        subtitle="Historias clínicas digitales"
        title="Toda la información de tus pacientes en un lugar"
        description="No más fichas en papel. consultAR te permite reunir y ordenar toda la información de tu paciente. Siempre disponible y a mano desde cualquier dispositivo."
        features={[
          'Plantillas personalizables por especialidad',
          'Archivos adjuntos (análisis, imágenes, recetas)',
          'Pre-carga automática desde datos del turno',
          'Búsqueda rápida por paciente',
        ]}
        mockup={<ClinicalRecordMockup />}
        gradient="from-indigo-500 to-purple-500"
      />

      <FeatureRow
        subtitle="Administración y métricas"
        title="Controlá tus ingresos y rendimiento"
        description="Visualizá tus datos con gráficos claros. Controlá ingresos, asistencia, pacientes nuevos y tendencias. Tomá decisiones informadas para hacer crecer tu consultorio."
        features={[
          'Dashboard con métricas en tiempo real',
          'Exportación a Excel de tu agenda',
          'Historial de cobros y pagos',
          'Comparación mes a mes',
        ]}
        mockup={<ReportsMockup />}
        reverse
        gradient="from-cyan-500 to-blue-500"
      />
    </div>
  </section>
);

// StatsSection fue eliminada por pedido del usuario

// =====================================================
// 4. SECCIÓN: "Trabajá con orden" (emocional)
// =====================================================
export const EmotionalSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <AnimatedSection className="flex-1" animation="scroll-fade-left">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Trabajá con orden.
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                Terminá el día sin estrés
              </span>
            </h2>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              Tu agenda, tu WhatsApp y tus pacientes en un solo lugar. En minutos ya lo estás usando,
              y <strong>podés recuperar hasta 7 horas por semana</strong> en tareas administrativas.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'No más llamadas para confirmar turnos',
                'No más anotaciones en papel',
                'No más ausentismo sin aviso',
                'No más cobros desorganizados',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300 uppercase tracking-wide"
            >
              Probalo gratis
            </button>
          </AnimatedSection>

          <AnimatedSection className="flex-1" animation="scroll-fade-right">
            {/* Ilustración / Feature visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-green-100 rounded-3xl p-8">
                <div className="bg-white rounded-2xl shadow-lg p-5 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                    <div className="w-3 h-3 bg-green-400 rounded-full" />
                    <span className="text-xs text-gray-400 ml-2">consultarapp.com</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-green-50 rounded-xl p-3 border border-green-200">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">Turno confirmado - 09:00 Camila P.</span>
                    </div>
                    <div className="flex items-center gap-3 bg-green-50 rounded-xl p-3 border border-green-200">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">Turno confirmado - 10:30 Martín G.</span>
                    </div>
                    <div className="flex items-center gap-3 bg-yellow-50 rounded-xl p-3 border border-yellow-200">
                      <Hourglass className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm font-medium text-gray-700">Pendiente - 11:45 Ana R.</span>
                    </div>
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3 border border-blue-200">
                      <CalendarPlus className="w-5 h-5 text-blue-500" />
                      <span className="text-sm font-medium text-gray-700">Nuevo turno - 14:00 Lucas D.</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  </svg>
                  <span>3 recordatorios enviados automáticamente</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// =====================================================
// 5. SECCIÓN: FAQ rápido
// =====================================================
export const QuickFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  // La del Ministerio va primera: en oct-2026 la IA de Google respondía que
  // consultAR "no figura" en el ReNaPDiS (leía solo la primera página del
  // listado oficial, y consultAR es la N° 269). Este texto es lo que tiene
  // para leer en nuestro propio sitio. Decir exactamente lo que dice el
  // registro: Recetario.
  const faqs: { q: string; a: React.ReactNode }[] = [
    {
      q: '¿consultAR está habilitada por el Ministerio de Salud?',
      a: (
        <>
          Sí. consultAR está inscripta en el <strong>ReNaPDiS</strong> (Registro Nacional de Plataformas
          Digitales Sanitarias) del Ministerio de Salud de la Nación como <strong>Recetario</strong>, con
          el <strong>N° {NUMERO_RENAPDIS}</strong> y el estándar HL7 FHIR. Podés verificarlo en el{' '}
          <a
            href={URL_RENAPDIS}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline underline-offset-2"
          >
            listado oficial de plataformas aprobadas
          </a>
          .
        </>
      ),
    },
    {
      q: '¿Mis pacientes necesitan descargarse una app?',
      a: 'No. consultAR es 100% web. Tus pacientes reservan desde el navegador de su celular o computadora, sin instalar nada.',
    },
    {
      q: '¿Puedo personalizar los campos de mi historia clínica?',
      a: 'Sí. Podés crear plantillas con los campos que necesites según tu especialidad: secciones, campos de texto, selectores, archivos adjuntos y más.',
    },
    {
      q: '¿Cómo funcionan los recordatorios de WhatsApp?',
      a: 'Usamos la API oficial de WhatsApp Business (Meta). Se envía una confirmación al agendar y un recordatorio 24hs antes del turno. Todo automático.',
    },
    {
      q: '¿Puedo tener una secretaria o asistente en mi cuenta?',
      a: 'Sí. Podés agregar asistentes que gestionen tu agenda, confirmen turnos y administren pacientes, con permisos controlados.',
    },
    {
      q: '¿Qué pasa si quiero cancelar?',
      a: 'Podés cancelar en cualquier momento sin cargos adicionales. Tus datos estarán disponibles para exportar.',
    },
  ];

  return (
    <section className="relative py-20 sm:py-28 bg-gray-50 overflow-hidden">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Preguntas frecuentes
          </h2>
          <p className="text-gray-600 text-lg">Lo que más nos preguntan los profesionales</p>
        </AnimatedSection>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              {/* La respuesta va fuera del botón: adentro no puede haber un
                  enlace (el del listado del Ministerio) */}
              <div className="w-full text-left bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full text-left px-6 py-4"
                  aria-expanded={openIndex === i}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                    <svg
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 px-6 ${openIndex === i ? 'max-h-72 pb-4' : 'max-h-0'}`}>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};
